import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react'
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import "../../Styles/pages/transactions.scss";
import "../../Styles/pages/payments.scss";
import "../../Styles/pages/activities.scss";
import "../../Styles/pages/report.scss"
import "../../Styles/pages/dashboard.scss";
import "../../Styles/pages/subscriptionDetail.scss"
import "../../Styles/pages/customers.scss"
import DefMembershipActions from "../../Redux/actions/defMembership-actions";
import PlanGroupActions from "../../Redux/transition/plan-group/plan-group.reducer";
import PaymentTrackActions from "../../Redux/transition/payment-track/payment-track.reducer";
import PaymentScheduleActions from '../../Redux/transition/payment-schedule/payment-schedule.reducer';
import BusinessCustomersActions from '../../Redux/transition/customers/business-customer.reducer';
import { normalizeSegmentsList } from '../Customers/Segments/segmentUtils';
import { ActionBar } from '../Payment/actionBar';
import CustomDataTable from "../../Components/CustomDataTable";
import { Toast } from "primereact/toast";
import { Paginator } from 'primereact/paginator';
import { useDateFormatter } from "../../Components/FormatDateTime";
import { useNavigate, useSearchParams } from "react-router-dom";
import { formatTextUppercase, removePlusFromNum } from '../../Components/CleanInputs';
import { FormatCurrency, useCurrencyConstants } from '../../Components/CurrencyFormatter';
import { getDateRange, resolveLifetimeStart } from '../../Components/DateRange';
import { fetchPaymentsForBcIds, parseBcIdsParam, resolveSubscribersByQuery } from '../Payment/subscriberSearch';
import moment from 'moment';

const PAYMENTS_MOBILE_BREAKPOINT = 1050;

const MyPayments = () => {
    const { formatDate, formatApiDate } = useDateFormatter();
    const { t } = useTranslation("common");
    const toast = useRef(null);
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { currencyFormat, currencySymbol } = useCurrencyConstants();

    const [calendarDates, setCalendarDates] = useState(null);
    const [fetchingExternalCsv, setFetchingExternalCsv] = useState(false);
    const [fetchingExternalPdf, setFetchingExternalPdf] = useState(false);
    const [isMobileStack, setIsMobileStack] = useState(
        () => typeof window !== "undefined" && window.innerWidth <= PAYMENTS_MOBILE_BREAKPOINT
    );
    const [showSubscriberName, setShowSubscriberName] = useState('');
    const [nameCleared, setNameCleared] = useState(false);
    const [showCustomDate, setShowCustomDate] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [bcId, setBcId] = useState('');
    const [selectedSegment, setSelectedSegment] = useState(null);
    const [selectedTags, setSelectedTags] = useState(null);
    const [selectedExternalMode, setSelectedExternalMode] = useState(null);
    const [pendingSubscriberSearch, setPendingSubscriberSearch] = useState(false);
    const [subscriberNotFound, setSubscriberNotFound] = useState(false);
    const [listRefreshToken, setListRefreshToken] = useState(0);
    const [matchedBcIds, setMatchedBcIds] = useState([]);
    const [subscriberSearchPage, setSubscriberSearchPage] = useState(null);
    const [fetchingSubscriberPayments, setFetchingSubscriberPayments] = useState(false);
    const matchedBcIdsKey = useMemo(() => matchedBcIds.join('|'), [matchedBcIds]);

    const periods = useMemo(() => [
        { code: "today", name: "Today" },
        { code: "this_month", name: "This Month" },
        { code: "last_week", name: "Last Week" },
        { code: "last_month", name: "Last Month" },
        { code: "last_3_months", name: "Last 3 Months" },
        { code: "last_6_months", name: "Last 6 Months" },
        { code: "last_12_months", name: "Last 12 Months" },
        { code: "year_to_date", name: "Year to date" },
        { code: "lifetime", name: "Lifetime" },
        { code: "custom", name: "Custom Date" }
    ], []);

    const defaultPeriodOption = periods[1];
    const [periodSelection, setPeriodSelection] = useState(defaultPeriodOption);

    const [state, setState] = useState({
        subscriberInfo: {},
        business: {},
        first: 0,
        size: 15,
        page: 0,
        stateExternalTotals: {},
        treePlanSelected: null,
        treeProductSelected: null,
        allProductsObject: [],
    });

    const { defMembership } = useSelector((state) => state.defMembership);
    const business = useSelector((state) => state.businesses.business);
    const fetchingExternal = useSelector((state) => state.paymentTracks.fetchingExternalPayments);
    const externalPayments = useSelector((state) => state.paymentTracks.externalPaymentTracks);
    const planGroupsGraphedActive = useSelector((state) => state.planGroups.planGroupsGraphedActive);
    const planGroupsGraphedAll = useSelector((state) => state.planGroups.planGroupsGraphedAll);
    const allPlanGroups = useSelector((state) => state.planGroups.planGroups);
    const businessCustomerRecord = useSelector((state) => state.businessCustomers.businessCustomerRecord);
    const paymentExternalCsv = useSelector((state) => state.paymentSchedules.paymentExternalCsv);
    const paymentExternalPdf = useSelector((state) => state.paymentSchedules.paymentExternalPdf);
    const fetchingPaymentExternalCsv = useSelector((state) => state.paymentSchedules.fetchingPaymentExternalCsv);
    const fetchingPaymentExternalPdf = useSelector((state) => state.paymentSchedules.fetchingPaymentExternalPdf);
    const paymentExternalCsvError = useSelector((state) => state.paymentSchedules.errorFetchingPaymentExternalCsv);
    const paymentExternalPdfError = useSelector((state) => state.paymentSchedules.errorFetchingPaymentExternalPdf);
    const externalPaymentTotals = useSelector((state) => state.paymentSchedules.externalPaymentTotals);
    const fetchingExternalPaymentTotals = useSelector((state) => state.paymentSchedules.fetchingExternalPaymentTotals);
    const { segmentSuccess, tagGroupsGraphed } = useSelector((state) => state.businessCustomers);
    const segmentList = useMemo(() => normalizeSegmentsList(segmentSuccess), [segmentSuccess]);

    const getMembership = () => dispatch(DefMembershipActions.defMembershipRequest());
    const getActivePlanGroups = useCallback((businessId) => { dispatch(PlanGroupActions.planGroupGraphedActiveRequest(businessId)) }, [dispatch]);
    const getAllPlanGroups = useCallback((businessId, options) => { dispatch(PlanGroupActions.planGroupAllRequest(businessId, options)) }, [dispatch]);
    const getExternalPayments = useCallback((businessId, filter, options) => dispatch(PaymentTrackActions.externalPaymentTrackRequest(businessId, filter, options)), [dispatch]);
    const getExternalTotals = useCallback((businessId, query, options) => dispatch(PaymentScheduleActions.externalPaymentTotalsRequest(businessId, query, options)), [dispatch]);
    const getBusinessCustomerRecord = useCallback((businessCustomerId) => dispatch(BusinessCustomersActions.businessCustomerRecordRequest(businessCustomerId)), [dispatch]);
    const getExternalPaymentCsv = useCallback((businessId, query, options) => dispatch(PaymentScheduleActions.paymentExternalCsvRequest(businessId, query, options)), [dispatch]);
    const getExternalPaymentPdf = useCallback((businessId, membershipId, query, options) => dispatch(PaymentScheduleActions.paymentExternalPdfRequest(businessId, membershipId, query, options)), [dispatch]);

    const getTodayStr = () => moment().format('YYYY-MM-DD');

    const getUrlParams = (source = window.location.search) => {
        const params = new URLSearchParams(source);
        params.delete('type');
        params.delete('membershipId');
        return params;
    };

    const externalModeFilterOptions = useMemo(() => [
        { name: t("payments.external_mode_cash"), code: "CASH" },
        { name: t("payments.external_mode_mobile_money"), code: "MOBILE_MONEY" },
        { name: t("payments.external_mode_bank_transfer"), code: "BANK_TRANSFER" },
        { name: t("payments.external_mode_cheque"), code: "CHEQUE" },
        { name: t("payments.external_mode_unknown"), code: "UNKNOWN" },
    ], [t]);

    const dropdownExportItems = [
        { name: t("payments.download_as_pdf"), code: "PDF", alias: t("payments.pdf_hint") },
        { name: t("payments.download_as_csv"), code: "CSV", alias: t("payments.csv_hint") },
    ];

    const membershipId = defMembership?.id;
    const businessId = defMembership?.business?.id;
    const membershipUserId = defMembership?.user?.id;
    const periodCode = periodSelection?.code;
    const filterStartDate = searchParams.get('startDate');
    const filterEndDate = searchParams.get('endDate');
    const filterProduct = searchParams.get('product');
    const filterPlan = searchParams.get('plan');
    const filterSearchText = searchParams.get('searchText');
    const filterBcId = searchParams.get('bcId');
    const filterSegmentId = searchParams.get('segmentId');
    const filterTags = searchParams.get('tags');
    const filterExternalMode = searchParams.get('externalMode');
    const lifetimeStart = useMemo(
        () => resolveLifetimeStart({
            selected: state.treePlanSelected || state.treeProductSelected,
            graphed: planGroupsGraphedAll || planGroupsGraphedActive,
        }),
        [state.treePlanSelected, state.treeProductSelected, planGroupsGraphedAll, planGroupsGraphedActive]
    );
    const page = state.page;
    const pageSize = state.size;

    useEffect(() => {
        const mediaQuery = window.matchMedia(`(max-width: ${PAYMENTS_MOBILE_BREAKPOINT}px)`);
        const onBreakpointChange = (event) => setIsMobileStack(event.matches);
        onBreakpointChange(mediaQuery);
        mediaQuery.addEventListener("change", onBreakpointChange);
        return () => mediaQuery.removeEventListener("change", onBreakpointChange);
    }, []);

    useEffect(() => {
        if (!defMembership) getMembership();
        if (defMembership) {
            getActivePlanGroups(defMembership.business.id);
            getAllPlanGroups(defMembership.business.id);
            dispatch(
                BusinessCustomersActions.fetchingSegmentRequest(defMembership.business.id, {
                    page: 0,
                    size: 1000,
                    sort: "createdDate,desc",
                })
            );
            dispatch(BusinessCustomersActions.tagGroupGraphedRequest(defMembership.business.id));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [businessId]);

    useEffect(() => {
        if (allPlanGroups) {
            setState((prev) => ({ ...prev, allProductsObject: allPlanGroups }));
        }
        if (business) {
            setState((prev) => ({ ...prev, business }));
        }
    }, [allPlanGroups, business, planGroupsGraphedActive]);

    useEffect(() => {
        if (!membershipId) return;

        const params = getUrlParams(searchParams);
        let changed = false;

        if (searchParams.has('type') || searchParams.has('membershipId')) {
            changed = true;
        }

        if (periodSelection && periodCode !== 'custom') {
            const { startDate: rangeStart, endDate: rangeEnd } = getDateRange(periodCode, { returnRange: true, lifetimeStart });
            if (rangeStart && rangeEnd) {
                const expectedStart = formatApiDate(rangeStart);
                const expectedEnd = formatApiDate(rangeEnd) || getTodayStr();

                if (params.get('startDate') !== expectedStart) {
                    params.set('startDate', expectedStart);
                    changed = true;
                }
                if (params.get('endDate') !== expectedEnd) {
                    params.set('endDate', expectedEnd);
                    changed = true;
                }
            }
        } else if (!params.get('startDate') || !params.get('endDate')) {
            const { startDate: rangeStart, endDate: rangeEnd } = getDateRange(defaultPeriodOption, { returnRange: true });
            params.set('startDate', formatApiDate(rangeStart));
            params.set('endDate', formatApiDate(rangeEnd) || getTodayStr());
            changed = true;
        }

        if (changed) setSearchParams(params, { replace: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [periodCode, membershipId, searchParams, lifetimeStart]);

    // Fetch manually recorded payments for this profile whenever filters/dates change
    useEffect(() => {
        if (!businessId || !membershipId || !filterStartDate || !filterEndDate) return;
        if (subscriberNotFound) return;

        let queryParams = `?membershipId=${encodeURIComponent(membershipId)}&`;
        if (filterProduct) queryParams += `groupId=${filterProduct}&`;
        if (filterPlan) queryParams += `planId=${filterPlan}&`;
        if (filterSegmentId) queryParams += `segmentId=${filterSegmentId}&`;
        if (filterExternalMode) queryParams += `externalMode=${encodeURIComponent(filterExternalMode)}&`;
        if (filterTags) queryParams += `tags=${filterTags}&`;
        queryParams += `startDate=${filterStartDate}&endDate=${filterEndDate}`;

        if (matchedBcIds.length > 0) {
            let cancelled = false;
            setFetchingSubscriberPayments(true);
            fetchPaymentsForBcIds({
                businessId,
                type: "external",
                baseFilter: queryParams,
                bcIds: matchedBcIds,
                page,
                size: pageSize,
            }).then(({ pageData, totals }) => {
                if (cancelled) return;
                setSubscriberSearchPage(pageData);
                setState((prev) => ({ ...prev, stateExternalTotals: totals }));
            }).catch(() => {
                if (cancelled) return;
                setSubscriberSearchPage({ content: [], totalElements: 0 });
            }).finally(() => {
                setFetchingSubscriberPayments(false);
            });
            return () => { cancelled = true; };
        }

        setFetchingSubscriberPayments(false);
        setSubscriberSearchPage(null);

        if (filterBcId) {
            queryParams += `bcId=${encodeURIComponent(filterBcId)}&`;
            setBcId(filterBcId);
        } else {
            setBcId('');
            if (filterSearchText) queryParams += `searchText=${encodeURIComponent(filterSearchText)}&`;
        }

        const pagination = {
            page,
            size: pageSize,
            sort: "externallyReceivedOn,desc"
        };

        getExternalPayments(businessId, queryParams, pagination);
        getExternalTotals(businessId, queryParams, pagination);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [membershipId, businessId, filterStartDate, filterEndDate, filterProduct, filterPlan, filterSearchText,
        filterBcId, filterSegmentId, filterTags, filterExternalMode, page, pageSize, subscriberNotFound, listRefreshToken,
        matchedBcIdsKey
    ]);

    useEffect(() => {
        if (!fetchingExternalPaymentTotals && externalPaymentTotals) {
            setState((prev) => ({ ...prev, stateExternalTotals: externalPaymentTotals }));
        }
    }, [externalPaymentTotals, fetchingExternalPaymentTotals]);

    useEffect(() => {
        if (filterSearchText) setSearchText(filterSearchText);

        if (filterSegmentId && segmentList.length) {
            setSelectedSegment(segmentList.find((item) => String(item.id) === String(filterSegmentId)) || null);
        } else if (!filterSegmentId) setSelectedSegment(null);

        if (filterTags) setSelectedTags(filterTags.split("|").map(Number));
        else setSelectedTags(null);

        if (filterExternalMode) {
            setSelectedExternalMode(externalModeFilterOptions.find((option) => option.code === filterExternalMode) || null);
        } else setSelectedExternalMode(null);
    }, [
        filterSearchText, filterSegmentId, filterTags, filterExternalMode, filterProduct, filterPlan, segmentList, externalModeFilterOptions,
    ]);

    useEffect(() => {
        if (bcId && !String(bcId).includes('|')) getBusinessCustomerRecord(bcId);
    }, [bcId, getBusinessCustomerRecord]);

    useEffect(() => {
        if (bcId && !String(bcId).includes('|') && businessCustomerRecord) {
            setState((prev) => ({ ...prev, subscriberInfo: businessCustomerRecord }));
        }
    }, [bcId, businessCustomerRecord]);

    // Restore from URL using bcId only (no q)
    useEffect(() => {
        const bcIdFromUrl = searchParams.get('bcId');
        const bcIdsFromUrl = parseBcIdsParam(searchParams.get('bcIds'));

        if (bcIdsFromUrl.length) {
            setMatchedBcIds(bcIdsFromUrl);
            setBcId(bcIdsFromUrl.length === 1 ? bcIdsFromUrl[0] : '');
            return;
        }

        if (bcIdFromUrl) {
            setMatchedBcIds((prev) => (
                prev.length === 1 && String(prev[0]) === String(bcIdFromUrl) ? prev : [bcIdFromUrl]
            ));
            setBcId(bcIdFromUrl);
            if (businessCustomerRecord?.name && String(businessCustomerRecord.id) === String(bcIdFromUrl)) {
                setSearchText((current) => current || businessCustomerRecord.name);
            }
            return;
        }

        setMatchedBcIds((prev) => (prev.length ? [] : prev));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams, businessCustomerRecord]);

    useEffect(() => {
        if (nameCleared) {
            const params = getUrlParams(searchParams);
            params.delete('bcId');
            params.delete('bcIds');
            params.delete('searchText');
            params.delete('q');
            setSearchParams(params);
            setSearchText('');
            setShowSubscriberName('');
            setBcId('');
            setSubscriberNotFound(false);
            setPendingSubscriberSearch(false);
            setMatchedBcIds([]);
            setSubscriberSearchPage(null);
            setListRefreshToken((token) => token + 1);
            setNameCleared(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [nameCleared]);

    useEffect(() => {
        const params = getUrlParams();
        const selectedPlan = state.treePlanSelected;
        const selectedProduct = state.treeProductSelected;
        if (selectedPlan?.id) {
            params.set('plan', selectedPlan.id);
            params.delete('product');
        } else if (!selectedPlan) {
            params.delete('plan');
        }
        if (selectedProduct?.id) {
            params.set('product', selectedProduct.id);
            params.delete('plan');
        } else if (!selectedProduct) {
            params.delete('product');
        }
        setSearchParams(params);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state.treePlanSelected, state.treeProductSelected]);

    const downloadCsv = (blobData, filename) => {
        const blob = new Blob([blobData], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
    };

    const downloadPdf = (blobData, filename) => {
        const blob = new Blob([blobData], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
    };

    useEffect(() => {
        if (fetchingExternalCsv && !fetchingPaymentExternalCsv && paymentExternalCsv) {
            downloadCsv(paymentExternalCsv, t("payments.external_csv"));
            setFetchingExternalCsv(false);
        }
        if (fetchingExternalCsv && !fetchingPaymentExternalCsv && paymentExternalCsvError) {
            toast.current?.show({ severity: 'error', summary: t("error"), detail: t("payments.external_csv"), life: 3000 });
            setFetchingExternalCsv(false);
        }
        if (fetchingExternalPdf && !fetchingPaymentExternalPdf && paymentExternalPdf) {
            downloadPdf(paymentExternalPdf, t("payments.external_pdf"));
            setFetchingExternalPdf(false);
        }
        if (fetchingExternalPdf && !fetchingPaymentExternalPdf && paymentExternalPdfError) {
            toast.current?.show({ severity: 'error', summary: t("error"), detail: t("payments.external_pdf"), life: 3000 });
            setFetchingExternalPdf(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fetchingPaymentExternalCsv, fetchingPaymentExternalPdf, paymentExternalCsv, paymentExternalPdf, paymentExternalCsvError, paymentExternalPdfError]);

    const buildExportQuery = () => {
        const productCode = filterProduct || '';
        const planCode = filterPlan || '';
        const segmentCode = filterSegmentId || '';
        const tagsCode = filterTags || '';
        const startDate = filterStartDate;
        const endDate = filterEndDate;
        const text = filterSearchText || '';
        const id = filterBcId || '';
        const externalMode = filterExternalMode || '';

        const params = [];
        if (startDate) params.push(`startDate=${startDate}`);
        if (endDate) params.push(`endDate=${endDate}`);
        if (planCode) params.push(`planId=${planCode}`);
        else if (productCode) params.push(`groupId=${productCode}`);
        if (segmentCode) params.push(`segmentId=${segmentCode}`);
        if (tagsCode) params.push(`tags=${tagsCode}`);
        if (id) params.push(`bcId=${id}`);
        else if (text) params.push(`searchText=${text}`);
        if (externalMode) params.push(`externalMode=${externalMode}`);
        if (membershipId) params.push(`recordedById=${encodeURIComponent(membershipId)}`);
        return params.join('&');
    };

    const getPaymentCsvWithFilters = () => {
        getExternalPaymentCsv(businessId, buildExportQuery());
        setFetchingExternalCsv(true);
    };

    const getPaymentPdfWithFilters = () => {
        getExternalPaymentPdf(businessId, membershipId, buildExportQuery(), { sort: "externallyReceivedOn,desc" });
        setFetchingExternalPdf(true);
    };

    const exportItemsTemplate = (option) => (
        <section className='flex flex-column mb-1' onClick={() => {
            if (option?.code === "PDF") getPaymentPdfWithFilters();
            else getPaymentCsvWithFilters();
        }}>
            <div className='dropdown-export-temp pt-1'>
                <p className='m-0 option-name'>{option.name}</p>
                <i className='pi pi-download' />
            </div>
            <p className='m-0 option-alias'>{option.alias}</p>
        </section>
    );

    const handlePeriodChange = (selectedPeriod) => {
        const periodValue = selectedPeriod.value;
        setPeriodSelection(periodValue);
        const params = getUrlParams();

        if (periodValue.code === 'custom') {
            setShowCustomDate(true);
            return;
        }

        setShowCustomDate(false);
        setCalendarDates(null);
        const { startDate: chosenStart, endDate: chosenEnd } = getDateRange(periodValue.code, { returnRange: true, lifetimeStart });
        if (!chosenStart || !chosenEnd) return;
        params.set('startDate', formatApiDate(chosenStart));
        params.set('endDate', formatApiDate(chosenEnd) || getTodayStr());
        setSearchParams(params);
    };

    const handleCalendarSelect = (e) => {
        const value = e.value;
        setCalendarDates(value);
        if (Array.isArray(value) && value.length === 2 && value[0] && value[1]) {
            const params = getUrlParams(searchParams);
            params.set('startDate', formatApiDate(value[0]));
            params.set('endDate', formatApiDate(value[1]));
            setSearchParams(params);
            setShowCustomDate(false);
        }
    };

    const onClearButtonClick = () => {
        const { startDate: defaultStart, endDate: defaultEnd } = getDateRange(defaultPeriodOption, { returnRange: true });
        const params = getUrlParams();
        params.set('startDate', formatApiDate(defaultStart));
        params.set('endDate', formatApiDate(defaultEnd) || getTodayStr());
        setSearchParams(params);
        setPeriodSelection(defaultPeriodOption);
        setShowCustomDate(false);
        setCalendarDates(null);
    };

    const handleSearchChange = (e) => {
        const value = e?.value ?? e?.target?.value ?? '';
        if (value && typeof value === 'object') {
            setSearchText(value.name || '');
            return;
        }

        const next = String(value ?? '');
        const currentQuery = typeof searchText === 'string' ? searchText : (searchText?.name || '');
        if (next === currentQuery || (showSubscriberName && next === showSubscriberName)) return;

        const params = getUrlParams();
        const hadActiveSubscriberFilter = Boolean(
            subscriberNotFound
            || matchedBcIds.length
            || showSubscriberName
            || params.has('bcId')
            || params.has('bcIds')
            || params.has('searchText')
            || params.has('q')
        );

        setSearchText(value);
        setSubscriberNotFound(false);
        setMatchedBcIds([]);
        setSubscriberSearchPage(null);

        if (params.has('bcId') || params.has('bcIds') || params.has('searchText') || params.has('q') || showSubscriberName) {
            params.delete('bcId');
            params.delete('bcIds');
            params.delete('searchText');
            params.delete('q');
            setSearchParams(params);
            setBcId('');
            setShowSubscriberName('');
        } else if (hadActiveSubscriberFilter) {
            setListRefreshToken((token) => token + 1);
        }
    };

    const handleSearchSubmit = async (submittedValue) => {
        const fromField = typeof submittedValue === 'string' ? submittedValue : '';
        const currentQuery = (typeof searchText === 'string' ? searchText : searchText?.name) || showSubscriberName || '';
        const query = fromField.trim() || currentQuery;
        const normalizedQuery = String(query).trim();
        if (normalizedQuery.length < 3 || !businessId || pendingSubscriberSearch) {
            return;
        }

        const repeatOfCurrent = matchedBcIds.length > 0 && (
            normalizedQuery.toLowerCase() === String(currentQuery).trim().toLowerCase()
            || normalizedQuery.toLowerCase() === String(showSubscriberName || '').trim().toLowerCase()
        );

        setState((prev) => ({ ...prev, page: 0, first: 0 }));
        setSubscriberNotFound(false);
        setPendingSubscriberSearch(true);
        setSearchText(normalizedQuery);

        try {
            const { matches, bcIds, bcIdsParam } = await resolveSubscribersByQuery(businessId, normalizedQuery);

            if (!bcIds.length || !matches.length) {
                setSearchText(normalizedQuery);
                if (repeatOfCurrent) return;

                setSubscriberNotFound(true);
                setMatchedBcIds([]);
                setBcId('');
                setShowSubscriberName('');
                const params = getUrlParams();
                params.delete('bcId');
                params.delete('bcIds');
                params.delete('searchText');
                params.delete('q');
                setSearchParams(params);
                return;
            }

            const nextKey = bcIds.map(String).join('|');
            const sameCustomers = nextKey === matchedBcIdsKey;

            setSubscriberNotFound(false);
            setSearchText(normalizedQuery);
            setShowSubscriberName('');
            setMatchedBcIds(bcIds);
            setBcId(bcIds.length === 1 ? bcIds[0] : '');

            const params = getUrlParams();
            params.delete('searchText');
            params.delete('q');
            if (bcIds.length === 1) {
                params.set('bcId', String(bcIds[0]));
                params.delete('bcIds');
            } else {
                params.set('bcIds', bcIdsParam);
                params.delete('bcId');
            }
            setSearchParams(params);
            if (sameCustomers) {
                setListRefreshToken((token) => token + 1);
            }
        } catch (error) {
            setSearchText(normalizedQuery);
            if (repeatOfCurrent) return;

            setSubscriberNotFound(true);
            setMatchedBcIds([]);
            setBcId('');
            setShowSubscriberName('');
        } finally {
            setPendingSubscriberSearch(false);
        }
    };

    const handleSegmentFilter = (e) => {
        const value = e.value;
        const params = getUrlParams();
        if (value?.id) params.set('segmentId', value.id);
        else params.delete('segmentId');
        setSearchParams(params);
    };

    const handleTagSelection = (e) => {
        const tags = e.value;
        setSelectedTags(tags);
        const params = getUrlParams(searchParams);
        if (tags?.length) params.set('tags', tags.join('|'));
        else params.delete('tags');
        setSearchParams(params);
    };

    const handleExternalModeSelect = (e) => {
        const val = e?.value;
        setSelectedExternalMode(val);
        const params = getUrlParams();
        if (val?.code) params.set('externalMode', val.code);
        else params.delete('externalMode');
        setSearchParams(params);
    };

    const tagsOptions = useMemo(() => {
        if (!tagGroupsGraphed?.groups) return [];
        return tagGroupsGraphed.groups
            .filter((group) => group?.tags?.length)
            .map((group) => ({
                label: group.name,
                items: (group.tags || [])
                    .filter((tag) => tag?.id && tag?.name)
                    .map((tag) => ({ label: tag.name, value: tag.id })),
            }));
    }, [tagGroupsGraphed]);

    const filteredPayments = useMemo(() => {
        const content = externalPayments?.content || [];
        const myMembershipId = membershipId != null ? String(membershipId) : null;
        const myUserId = membershipUserId != null ? String(membershipUserId) : null;

        return content.filter((payment) => {
            // Keep only payments marked/recorded by this profile when the API includes that field
            const recordedBy = payment.externallyReceivedBy;
            if (!recordedBy) return true;

            const recordedByMembershipId = recordedBy.id != null ? String(recordedBy.id) : null;
            const recordedByUserId = recordedBy.user?.id != null ? String(recordedBy.user.id) : null;
            const matchesMembership = myMembershipId && recordedByMembershipId === myMembershipId;
            const matchesUser = myUserId && recordedByUserId === myUserId;
            return matchesMembership || matchesUser;
        });
    }, [externalPayments, membershipId, membershipUserId]);

    const totals = useMemo(() => {
        if (state.stateExternalTotals && Object.keys(state.stateExternalTotals).length) {
            return state.stateExternalTotals;
        }
        const next = { total: 0, cash: 0, mobileMoney: 0, bankTransfer: 0, cheque: 0, unknown: 0 };
        filteredPayments.forEach((payment) => {
            const amount = Number(payment.amount) || 0;
            next.total += amount;
            const mode = String(payment.extReceivedMode || 'UNKNOWN').toUpperCase();
            if (mode === 'CASH') next.cash += amount;
            else if (mode === 'MOBILE_MONEY') next.mobileMoney += amount;
            else if (mode === 'BANK_TRANSFER') next.bankTransfer += amount;
            else if (mode === 'CHEQUE') next.cheque += amount;
            else next.unknown += amount;
        });
        return next;
    }, [filteredPayments, state.stateExternalTotals]);

    useEffect(() => {
        setState((prev) => (prev.page === 0 && prev.first === 0 ? prev : { ...prev, page: 0, first: 0 }));
    }, [
        filterStartDate,
        filterEndDate,
        filterExternalMode,
        filterSearchText,
        filterBcId,
        filterProduct,
        filterPlan,
        filterSegmentId,
        filterTags,
        matchedBcIdsKey,
    ]);

    const pageRows = subscriberNotFound
        ? []
        : matchedBcIds.length > 0
            ? (subscriberSearchPage?.content ?? [])
            : filteredPayments;
    const totalRecords = subscriberNotFound
        ? 0
        : matchedBcIds.length > 0
            ? (subscriberSearchPage?.totalElements ?? 0)
            : (externalPayments?.totalElements ?? filteredPayments.length);

    const subscriptionBodyTemplate = (rowData) => {
        if (!rowData) return null;
        return (
            <div>
                <div className='text-left cursor-pointer mb-2' onClick={() => navigate(`/crm/customers/customer/subscription/${rowData.subscriptionId}`)}>
                    {rowData.subscriptionRef ? <p className="detail-navigation space-beneath" id="sub_ref">{rowData.subscriptionRef}</p> : null}
                    <div className="flex" style={{ paddingBottom: "0.2rem" }}>
                        <p className="text-left policy mb-0">
                            {rowData.deductionPolicy === "TIER"
                                ? t("products.fixed_billing").toUpperCase()
                                : rowData.deductionPolicy === "SUBSCRIPTION"
                                    ? t("products.custom_billing").toUpperCase()
                                    : t('billing', { val: rowData.deductionPolicy })}
                        </p>
                    </div>
                    {rowData.planName ? <p className="plan-group-text plan_name space-beneath">{rowData.planName}</p> : null}
                </div>
                {rowData.customerNum && (rowData.busCustomerName || rowData.customerName) ? (
                    <div>
                        <p className="text-left cus-text space-beneath">{rowData.customerNum}</p>
                        <p className="ref-text text-left space-beneath" id='tierSub'>{rowData.busCustomerName || rowData.customerName}</p>
                    </div>
                ) : !rowData.customerNum && (rowData.busCustomerName || rowData.customerName) ? (
                    <p className="text-left cus-text space-beneath">{rowData.busCustomerName || rowData.customerName}</p>
                ) : null}
                {rowData.phoneNo ? <span className="ref-text" id='tierSub'>{removePlusFromNum(rowData.phoneNo)}</span> : null}

                {rowData.segmentName ?
                    <span className="text-xs segment-tag-div mt-1">{rowData.segmentName}</span> : null
                }
                {
                    Array.isArray(rowData.tags) && rowData.tags.length > 0 &&
                    <section className='flex gap-1'>
                        {rowData.tags.map((tag, index) => (
                            <div key={tag.id ?? tag.name ?? index} className='text-left tag-div-main tag-class mt-1'>
                                {tag.name}
                            </div>
                        ))}
                    </section>
                }
            </div>
        );
    };

    const paymentIdBodyTemplate = (rowData) => (
        rowData?.id ? <div><p className="detail-navigatio" id="transactionId">{rowData.id}</p></div> : null
    );

    const paymentDetsBodyTemplate = (rowData) => {
        if (!rowData?.externallyReceivedOn) return <div>--</div>;
        return (
            <div className="detail-navigatio" id="occured">
                <span className='date-time'>{formatDate(rowData.externallyReceivedOn)}</span>
                {rowData.extReceivedMode && <p className='pay-style m-0'>{formatTextUppercase(rowData.extReceivedMode)}</p>}
                <p className='phone-detail mt-0'>{rowData.extReceivedRef}</p>
            </div>
        );
    };

    const colAmountBodyTemplate = (rowData) => {
        if (!rowData) return null;
        return (
            <div className="col_amount_body">
                {rowData.flex === true ? (
                    <div>
                        <span style={{ display: "block", marginBottom: "0.5rem" }}>{t("products.flexible_amount")}</span>
                        {rowData.amount && <FormatCurrency amount={rowData.amount} currencySymbol={currencySymbol} currencyFormat={currencyFormat} className='transactions_data' />}
                    </div>
                ) : (
                    <div>
                        {rowData.amount ? <FormatCurrency amount={rowData.amount} currencySymbol={currencySymbol} currencyFormat={currencyFormat} className='transactions_data' /> : null}
                    </div>
                )}
            </div>
        );
    };

    const feesBodyTemplate = (rowData) => (
        <div className="notes_template">
            {rowData?.extReceivedNotes ? <p className="detail-navigatio" id="fees">{rowData.extReceivedNotes}</p> : '--'}
        </div>
    );

    const paymentsMobileSummaryTemplate = (rowData) => (
        <div className="payments-mobile-summary">
            <div className="payments-mobile-section">{paymentIdBodyTemplate(rowData)}</div>
            <div className="payments-mobile-section">{subscriptionBodyTemplate(rowData)}</div>
            <div className="payments-mobile-section">{paymentDetsBodyTemplate(rowData)}</div>
            <div className="payments-mobile-section">{colAmountBodyTemplate(rowData)}</div>
            <div className="payments-mobile-section payments-mobile-section-last">{feesBodyTemplate(rowData)}</div>
        </div>
    );

    const desktopColumns = [
        { field: "reference", header: t("payments.transaction_id"), className: "trans-id", body: paymentIdBodyTemplate, alignHeader: "left" },
        { field: "customerNum", header: t("payments.subscription_info"), className: "paymentSubsBodyTemplate", alignHeader: "left", body: subscriptionBodyTemplate },
        { field: "receivedAt", header: t("payments.payment-dets"), className: "occurred-on", body: paymentDetsBodyTemplate, alignHeader: "left" },
        { field: "amount", header: t("payments.amount"), body: colAmountBodyTemplate, alignHeader: "left", sort: true },
        { field: "fees", header: t("payments.note"), body: feesBodyTemplate, alignHeader: "left" },
    ];

    const columns = isMobileStack
        ? [{ field: "mobileSummary", header: t("payments.payment_id"), body: paymentsMobileSummaryTemplate, className: "payments-mobile-summary-column" }]
        : desktopColumns;

    const onPageChange = (e) => {
        if (e.pageCount <= 1) return;
        setState((prev) => ({ ...prev, first: e.first, page: e.page }));
    };

    const spinnerConditions = fetchingPaymentExternalPdf || fetchingPaymentExternalCsv;

    return (
        <div className='payments-screen'>
            <Toast ref={toast} />
            <ActionBar
                myPaymentsMode
                selectedCustomer={searchText}
                setSelectedCustomer={handleSearchChange}
                onSearchSubmit={handleSearchSubmit}
                onClear={onClearButtonClick}
                productSelection={state.treeProductSelected}
                planSelection={state.treePlanSelected}
                tagsOptions={tagsOptions}
                period={periodSelection}
                periods={periods}
                setPeriod={handlePeriodChange}
                filterStartDate={filterStartDate}
                filterEndDate={filterEndDate}
                total={totalRecords}
                showCustomDate={showCustomDate}
                showSubscriberName={showSubscriberName}
                setShowSubscriberName={setShowSubscriberName}
                calendarDates={calendarDates}
                handleCalendarSelect={handleCalendarSelect}
                externalModeFilterOptions={externalModeFilterOptions}
                selectedExternalMode={selectedExternalMode}
                handleExternalModeSelect={handleExternalModeSelect}
                segmentSuccess={segmentList}
                selectedSegment={selectedSegment}
                handleSegmentFilter={handleSegmentFilter}
                handleTagSelection={handleTagSelection}
                selectedTags={selectedTags}
                setNameCleared={setNameCleared}
                spinnerConditions={spinnerConditions}
                exportItemsTemplate={exportItemsTemplate}
                dropdownExportItems={dropdownExportItems}
                state={{ ...state, stateExternalTotals: totals }}
                setState={setState}
                currencySymbol={currencySymbol}
                currencyFormat={currencyFormat}
            />
            <CustomDataTable
                key={`my-payments-${isMobileStack}`}
                columns={columns}
                loading={fetchingExternal || fetchingExternalPaymentTotals || pendingSubscriberSearch || fetchingSubscriberPayments}
                responsiveLayout="stack"
                tableClassName="payments-data-table"
                value={pageRows}
            />
            <Paginator
                className="custom-paginator payments-paginator"
                rows={totalRecords === 0 ? 0 : state.size}
                totalRecords={totalRecords}
                first={state.first}
                onPageChange={onPageChange}
            />
        </div>
    );
};

export default MyPayments;